import { useState } from "react";
import { Contractor, HomeownerProject } from "./data";

interface ProjectInviteModalProps {
  contractor: Contractor;
  projects: HomeownerProject[];
  sending?: boolean;
  onSendInvite: (projectId: string, message: string) => void;
  onClose: () => void;
}

export function ProjectInviteModal({
  contractor,
  projects,
  sending = false,
  onSendInvite,
  onClose,
}: ProjectInviteModalProps) {
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [message, setMessage] = useState("");

  const handleSendInvite = () => {
    if (!selectedProjectId) {
      return;
    }
    onSendInvite(selectedProjectId, message);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 suggested-contractors-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-lg border border-border bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border px-6 py-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="mb-1 text-foreground font-[500] text-[15px]">Send Project Invite</div>
              <div className="text-muted-foreground">
                Invite {contractor.name} to bid on a project
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg px-3 py-1 text-foreground hover:bg-accent"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="px-6 py-6">
          <div className="mb-6">
            <label className="mb-3 block text-foreground">Select Project</label>
            <div className="space-y-3">
              {projects.length === 0 && (
                <div className="rounded-lg border border-border bg-background px-4 py-3 text-sm text-muted-foreground">
                  No projects found for this homeowner.
                </div>
              )}
              {projects.map((project) => {
                const isSelected = selectedProjectId === project.id;
                return (
                  <div
                    key={project.id}
                    onClick={() => setSelectedProjectId(project.id)}
                    className={`cursor-pointer rounded-lg border p-4 ${
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-border bg-background hover:bg-accent/50"
                    }`}
                  >
                    <div className="mb-1 flex items-start justify-between">
                      <div className="text-foreground">{project.name}</div>
                      <input
                        type="radio"
                        checked={isSelected}
                        onChange={() => setSelectedProjectId(project.id)}
                        className="h-4 w-4 cursor-pointer"
                      />
                    </div>
                    <div className="mb-3 text-foreground/70">
                      {project.description}
                    </div>
                    <div className="flex gap-6 text-muted-foreground">
                      <div>Budget: {project.budget}</div>
                      <div>Timeline: {project.timeline}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-6">
            <label className="mb-2 block text-foreground">
              Message to Contractor (Optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add any specific details or questions..."
              rows={4}
              className="w-full rounded-lg border border-border bg-input-background px-3 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        <div className="border-t border-border px-6 py-4">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="rounded-lg border border-border bg-background px-6 py-2 text-foreground hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={handleSendInvite}
              disabled={!selectedProjectId || sending}
              className={`rounded-lg px-6 py-2 ${
                selectedProjectId && !sending
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "cursor-not-allowed bg-muted text-muted-foreground"
              }`}
            >
              {sending ? "Sending..." : "Send Invite"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
